import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, ArrowRight, RotateCcw, Award, ChevronLeft } from "lucide-react";
import { rer, gradeNumeric } from "@/data/nutritionCases";
import { NumberResult, fmt0, fmt1 } from "@/components/nutrition/NutritionParts";

const rnd = (a, b) => a + Math.random() * (b - a);
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const r5 = (n) => Math.round(n / 5) * 5;
const w1 = (n) => +n.toFixed(1);

const FACTORS_DOG = [
  { f: 1.6, l: "neutered adult dog" },
  { f: 1.8, l: "intact adult dog" },
  { f: 2.0, l: "puppy over 4 months" },
  { f: 1.4, l: "inactive / weight-prone dog" },
];
const FACTORS_CAT = [
  { f: 1.2, l: "neutered adult cat" },
  { f: 1.4, l: "intact adult cat" },
  { f: 3.0, l: "queen in peak lactation" },
];
const DENSITY_DRY = [300, 330, 350, 360, 380, 410];
const DENSITY_CAN = [75, 95, 110];

// Procedurally generate a mixed quick-quiz.
export const makeQuiz = (n = 8) => {
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const type = pick(["rer", "mer", "portion_dry", "portion_can"]);
    const species = pick(["dog", "cat"]);
    const w = species === "dog" ? w1(rnd(4, 38)) : w1(rnd(2.5, 7));
    if (type === "rer") {
      out.push({
        prompt: `Calculate the RER for a ${w} kg ${species}.`,
        given: [["Weight", `${w} kg`]],
        unit: "kcal/day",
        answer: rer(w),
        decimals: false,
        working: `70 × ${w}^0.75`,
      });
    } else if (type === "mer") {
      const fo = species === "dog" ? pick(FACTORS_DOG) : pick(FACTORS_CAT);
      out.push({
        prompt: `Calculate the MER for a ${w} kg ${fo.l} (factor ×${fo.f}).`,
        given: [["Weight", `${w} kg`], ["Factor", `×${fo.f}`]],
        unit: "kcal/day",
        answer: rer(w) * fo.f,
        decimals: false,
        working: `70 × ${w}^0.75 × ${fo.f}`,
      });
    } else if (type === "portion_dry") {
      const kcal = r5(rnd(180, 1100));
      const d = pick(DENSITY_DRY);
      out.push({
        prompt: `Feed ${kcal} kcal/day on a dry food of ${d} kcal/100 g. How many grams per day?`,
        given: [["Energy to feed", `${kcal} kcal`], ["Energy density", `${d} kcal / 100 g`]],
        unit: "g/day",
        answer: (kcal / d) * 100,
        decimals: false,
        working: `${kcal} ÷ ${d} × 100`,
      });
    } else {
      const kcal = r5(rnd(150, 400));
      const d = pick(DENSITY_CAN);
      out.push({
        prompt: `Feed ${kcal} kcal/day. The canned food is ${d} kcal/can. How many cans per day?`,
        given: [["Energy to feed", `${kcal} kcal`], ["Energy density", `${d} kcal / can`]],
        unit: "cans/day",
        answer: kcal / d,
        decimals: true,
        working: `${kcal} ÷ ${d}`,
      });
    }
  }
  return out;
};

const band = (pct) =>
  pct >= 80
    ? { label: "Sharp", text: "text-emerald-600", ring: "border-emerald-300 bg-emerald-50" }
    : pct >= 60
    ? { label: "Getting there", text: "text-amber-600", ring: "border-amber-300 bg-amber-50" }
    : { label: "Keep drilling", text: "text-red-600", ring: "border-red-300 bg-red-50" };

export const NutritionQuiz = ({ onExit, count = 8 }) => {
  const [questions, setQuestions] = useState(() => makeQuiz(count));
  const [idx, setIdx] = useState(0);
  const [val, setVal] = useState("");
  const [res, setRes] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const q = questions[idx];
  const fmtAns = (a) => (q.decimals ? fmt1(a) : fmt0(a));

  const check = () => {
    if (val === "") return;
    const g = gradeNumeric(val, q.answer, 1);
    setRes(g);
    if (g.band === "correct") setScore((sc) => sc + 1);
  };
  const next = () => {
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
      setVal("");
      setRes(null);
    } else setFinished(true);
  };
  const restart = () => {
    setQuestions(makeQuiz(count));
    setIdx(0);
    setVal("");
    setRes(null);
    setScore(0);
    setFinished(false);
  };

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    const b = band(pct);
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm fade-up text-center max-w-xl mx-auto" data-testid="quiz-summary">
        <div className={`flex flex-col items-center justify-center w-24 h-24 rounded-2xl border-4 mx-auto ${b.ring}`}>
          <Award className={`w-5 h-5 ${b.text}`} />
          <span className={`text-4xl font-bold leading-none ${b.text}`} data-testid="quiz-percent">{pct}</span>
          <span className="text-[10px] text-slate-400 font-semibold">/100</span>
        </div>
        <div className={`text-xl font-bold mt-3 ${b.text}`} data-testid="quiz-band">{b.label}</div>
        <div className="text-sm text-slate-600 mt-1">
          You got <span className="font-semibold">{score}</span> of {questions.length} within ±10%.
        </div>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button onClick={restart} data-testid="quiz-restart-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            <RotateCcw className="w-4 h-4 mr-1.5" /> New quiz
          </Button>
          <Button onClick={onExit} variant="outline" data-testid="quiz-exit-btn" className="text-slate-600">
            <ChevronLeft className="w-4 h-4 mr-1.5" /> Back to start
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm fade-up max-w-xl mx-auto" data-testid="quiz-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold">
          <Zap className="w-3.5 h-3.5" /> Rapid quiz · {idx + 1} / {questions.length}
        </div>
        <div className="text-xs font-mono-clinical font-semibold text-slate-600" data-testid="quiz-score">
          Score {score}/{idx + (res ? 1 : 0)}
        </div>
      </div>

      <p className="text-lg font-semibold text-slate-900 leading-snug" data-testid="quiz-prompt">{q.prompt}</p>

      <div className="mt-4 grid grid-cols-2 gap-2">
        {q.given.map(([k, v]) => (
          <div key={k} className="rounded-md bg-slate-50 border border-slate-200 p-2.5">
            <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
            <div className="font-mono-clinical text-sm text-slate-900 font-semibold leading-tight mt-0.5">{v}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Input
          type="number"
          step="any"
          min="0"
          inputMode="decimal"
          placeholder="answer"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          disabled={!!res}
          onKeyDown={(e) => e.key === "Enter" && !res && check()}
          data-testid="quiz-input"
          className="w-36 font-mono-clinical"
        />
        <span className="text-sm text-slate-500">{q.unit}</span>
      </div>

      {res && (
        <div className="mt-3 space-y-1" data-testid="quiz-feedback">
          <NumberResult band={res.band} correctText={`${fmtAns(q.answer)} ${q.unit}`} testid="quiz-result" />
          <div className="text-[11px] text-slate-500 font-mono-clinical">working: {q.working} = {fmtAns(q.answer)}</div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        {!res ? (
          <Button onClick={check} disabled={val === ""} data-testid="quiz-check-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            Check
          </Button>
        ) : (
          <Button onClick={next} data-testid="quiz-next-btn" className="bg-[#0047AB] hover:bg-[#0a3f8f] text-white">
            {idx < questions.length - 1 ? "Next" : "See results"} <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        )}
      </div>
    </div>
  );
};
